import { useCallback } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";

import { renderErrorMessage } from "Services/LoggingService";
import { ExternalPhoneNumber } from "Types/ExternalPhoneNumber";

import {
    createTransferPhoneNumber as createTransferPhoneNumberGQL,
    updateTransferPhoneNumber as updateTransferPhoneNumberGQL,
    removeTransferPhoneNumber as removeTransferPhoneNumberGQL,
} from "../mutations";
import { getTransferPhoneNumbers, getTransferPhoneNumbersForTeams } from "../queries";

function stripNumber(number: string | undefined | null): string | undefined {
    if (!number) {
        return undefined;
    }
    const match = number.replace(/[^\d]/g, "").match(/1?(\d{10})/);
    return match ? match[1] : undefined;
}

function formatPhoneNumberForMutation(phoneNumber: string | undefined | null): string | undefined {
    const stripped = stripNumber(phoneNumber);
    return stripped ? `+1${stripped}` : undefined;
}

type Five9AttributesPayload = {
    routeType: string;
    routeValue: string;
    routeReason: string;
    additionalMetadata?: Record<string, string>;
};

function buildFive9Attributes(formData: any): Five9AttributesPayload | undefined {
    if (formData.integration !== "five9") {
        return undefined;
    }
    const { routeType, routeValue, routeReason, sipHeadersMap } = formData;
    return {
        routeType,
        routeValue,
        routeReason,
        additionalMetadata: sipHeadersMap,
    };
}

const refetchQueries = ["GetTransferPhoneNumbers"];

const EMPTY: Array<ExternalPhoneNumber> = [];

export const useExternalTransferPhoneNumbers = ({ queryParams = {}, isManagerContext = true } = {}) => {
    const [createTransferPhoneNumberMutation, { loading: isCreating }] = useMutation(createTransferPhoneNumberGQL, {
        notifyOnNetworkStatusChange: true,
        refetchQueries,
    });
    const [updateTransferPhoneNumberMutation, { loading: isUpdating }] = useMutation(updateTransferPhoneNumberGQL, {
        notifyOnNetworkStatusChange: true,
        refetchQueries,
    });
    const [removeTransferPhoneMutation, { loading: isDeleting }] = useMutation(removeTransferPhoneNumberGQL, {
        notifyOnNetworkStatusChange: true,
        refetchQueries,
    });

    const query = isManagerContext ? getTransferPhoneNumbers : getTransferPhoneNumbersForTeams;

    const { data, loading: isFetching } = useQuery(query, {
        fetchPolicy: "network-only",
        variables: { queryParams },
    });

    const createTransferPhoneNumber = useCallback(
        async (formData: any) => {
            try {
                const {
                    name,
                    phoneNumber,
                    teamIds,
                    integration,
                    profileAttributes,
                    externalId,
                    sipUri,
                    sipHeadersMap,
                    uuiHeadersMap,
                    profileAttributesMap,
                    endRecordingOnTransfer,
                    contactAttribute,
                    sipMethod,
                    isDynamicTransfer,
                } = formData;

                const five9 = buildFive9Attributes(formData);
                const isFive9 = integration === "five9";

                const createData = {
                    name,
                    phoneNumber: formatPhoneNumberForMutation(phoneNumber),
                    teamIds,
                    integration,
                    profileAttributes,
                    externalId,
                    sipUri,
                    sipHeadersMap: isFive9 ? undefined : sipHeadersMap,
                    uuiHeadersMap,
                    profileAttributesMap,
                    contactAttribute,
                    isDynamicTransfer,
                    attributes: {
                        endRecordingOnTransfer: endRecordingOnTransfer ?? false,
                        sipMethod,
                        ...(five9 ? { five9 } : {}),
                    },
                };

                const { data } = await createTransferPhoneNumberMutation({
                    variables: createData,
                });
                message.success("External transfer phone number successfully created!");
                return data?.createTransferPhoneNumber;
            } catch (error: any) {
                renderErrorMessage({
                    content: `Error creating external ${formData?.name} - ${error.message || error}.`,
                    error,
                });
            }
        },
        [createTransferPhoneNumberMutation]
    );

    const updateTransferPhoneNumber = useCallback(
        async (id: string, formData: any) => {
            try {
                const {
                    name,
                    phoneNumber,
                    teamIds,
                    integration,
                    profileAttributes,
                    externalId,
                    sipUri,
                    sipHeadersMap,
                    uuiHeadersMap,
                    profileAttributesMap,
                    endRecordingOnTransfer,
                    contactAttribute,
                    sipMethod,
                    isDynamicTransfer,
                } = formData;
                const five9 = buildFive9Attributes(formData);
                const isFive9 = integration === "five9";

                const updatedData = {
                    id: id,
                    name,
                    phoneNumber: formatPhoneNumberForMutation(phoneNumber),
                    teamIds: teamIds ?? [],
                    integration: integration ?? null,
                    profileAttributes: profileAttributes ?? [],
                    externalId: externalId ?? null,
                    sipUri: sipUri ?? null,
                    sipHeadersMap: isFive9 ? {} : (sipHeadersMap ?? {}),
                    uuiHeadersMap: uuiHeadersMap ?? {},
                    profileAttributesMap: profileAttributesMap ?? {},
                    contactAttribute: contactAttribute ?? null,
                    isDynamicTransfer: isDynamicTransfer ?? null,
                    attributes: {
                        endRecordingOnTransfer: endRecordingOnTransfer ?? false,
                        sipMethod,
                        ...(five9 ? { five9 } : {}),
                    },
                };

                const { data } = await updateTransferPhoneNumberMutation({
                    variables: updatedData,
                });
                message.success("External transfer phone number successfully updated!");
                return data?.updateTransferPhoneNumber;
            } catch (error: any) {
                renderErrorMessage({
                    content: `Error updating external ${formData?.name} - ${error.message || error}.`,
                    error,
                });
            }
        },
        [updateTransferPhoneNumberMutation]
    );

    const deleteTransferPhoneNumber = useCallback(
        async (id: string) => {
            await removeTransferPhoneMutation({ variables: { id } });
            message.success(`Successfully deleted the external transfer phone number.`);
        },
        [removeTransferPhoneMutation]
    );

    return {
        phoneNumbersData: data?.getTransferPhoneNumbers || EMPTY,
        createTransferPhoneNumber,
        updateTransferPhoneNumber,
        deleteTransferPhoneNumber,
        removeTransferPhone: removeTransferPhoneMutation,
        isFetching,
        isLoading: isCreating || isUpdating || isDeleting,
    };
};
